import { Box, Group, List, SimpleGrid, Text, ThemeIcon } from '@mantine/core';
import { Info, Sparkles } from 'lucide-react';
import type { ReactElement } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import StatBand from '@/design/StatBand';
import { SurfaceCard } from '@/design/SurfaceCard';
import { formatCompact } from '@/shared/analytics/MetricFormatting';
import type { EfficiencyImpact } from './InsightsTypes';

interface ModelCost {
    model: string;
    actual: number;
    optimized: number;
}

export interface CostModel {
    totalCost: number;
    optimizedTotal: number;
    estimatedWaste: number;
    blendedRatePerMTok: number;
    byModel: ModelCost[];
    recommendedActions: string[];
}

export function usd(value: number): string {
    if (value === 0) return '$0';
    const abs = Math.abs(value);
    if (abs < 1) return `$${value.toFixed(3)}`;
    if (abs < 100) return `$${value.toFixed(2)}`;
    return `$${Math.round(value).toLocaleString()}`;
}

/**
 * Cost model from the real allocated spend on the scorecard. `comparison[].cost` and
 * `totalCost` are the OLAP `allocated_cost` column (actual provider spend allocated per
 * turn by token share) — the same basis the Cost menu uses. Per-model rates are derived
 * empirically ($/token); "optimized" re-prices every token at the cheapest observed rate,
 * and waste is that model overkill plus refinement overhead priced at the same rate.
 */
export function computeCostModel(efficiency: EfficiencyImpact): CostModel {
    const totalCost = efficiency.totalCost;
    const totalTokens = efficiency.totalTokens;
    const priced = efficiency.comparison.filter((c) => c.tokens > 0 && c.cost > 0);
    const cheapestRate =
        priced.length > 0
            ? Math.min(...priced.map((c) => c.cost / c.tokens))
            : totalTokens > 0
              ? totalCost / totalTokens
              : 0;

    const byModel: ModelCost[] = efficiency.comparison
        .filter((c) => c.tokens > 0)
        .map((c) => ({ model: c.category, actual: c.cost, optimized: c.tokens * cheapestRate }))
        .sort((a, b) => b.actual - a.actual);

    const optimizedTotal = totalTokens * cheapestRate;
    const overkill = Math.max(0, totalCost - optimizedTotal);
    const wasteCost = efficiency.estimatedWastedTokens * cheapestRate;
    const estimatedWaste = overkill + wasteCost;
    const blendedRatePerMTok = totalTokens > 0 ? (totalCost / totalTokens) * 1_000_000 : 0;

    const recommendedActions: string[] = [];
    const topPremium = byModel.find((m) => m.actual - m.optimized > 0.0001);
    if (topPremium) {
        recommendedActions.push(
            `Route ${topPremium.model} work to a lighter model — about ${usd(topPremium.actual - topPremium.optimized)} of avoidable spend at your current volume.`,
        );
    }
    if (wasteCost > 0) {
        recommendedActions.push(
            `Cut refinement overhead (~${formatCompact(efficiency.estimatedWastedTokens)} tokens re-sent) — roughly ${usd(wasteCost)} you could recover.`,
        );
    }
    recommendedActions.push(
        'Batch related questions into a single session instead of restarting — restarts re-bill your full context each time.',
    );

    return { totalCost, optimizedTotal, estimatedWaste, blendedRatePerMTok, byModel, recommendedActions };
}

/** Cost Saving Opportunities — real allocated spend, where it's wasted, and how to trim it. */
export default function InsightsCostSection({ efficiency }: { efficiency: EfficiencyImpact }): ReactElement {
    // Chrome speaks brand — section icons/actions no longer carry a per-section hue.
    const tokens = { solid: 'var(--bm-color-brand-accent)', soft: 'var(--bm-color-brand-soft)', text: 'var(--bm-color-brand-accent)' };
    const cost = computeCostModel(efficiency);

    if (cost.totalCost === 0) {
        return (
            <SurfaceCard $padding="lg">
                <Group gap={12} align="flex-start" wrap="nowrap">
                    <ThemeIcon size={32} radius="xl" style={{ background: tokens.soft, color: tokens.text }}>
                        <Info size={17} />
                    </ThemeIcon>
                    <Box>
                        <Text fw={600} mb={4}>
                            No usage cost recorded this period
                        </Text>
                        <Text size="sm" c="dimmed" style={{ lineHeight: 1.55 }}>
                            No provider spend was allocated to your usage in this window (some tools bill by seat rather than by
                            token). Widen the date range to include token-metered usage.
                        </Text>
                    </Box>
                </Group>
            </SurfaceCard>
        );
    }

    return (
        <Box>
            <StatBand
                stats={[
                    { label: 'Total cost', value: usd(cost.totalCost), hint: 'real allocated spend' },
                    { label: 'Estimated waste', value: usd(cost.estimatedWaste), hint: 'model overkill + refinement overhead' },
                    { label: 'Blended rate', value: usd(cost.blendedRatePerMTok), hint: 'per 1M tokens' },
                ]}
            />
            <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md" mt="md">
                <SurfaceCard $padding="md">
                    <Text size="xs" c="dimmed" fw={700} tt="uppercase" mb="sm" style={{ fontSize: '9px', letterSpacing: '0.08em' }}>
                        Actual vs optimized cost by model
                    </Text>
                    <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={cost.byModel} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                            <CartesianGrid vertical={false} stroke="var(--bm-color-border-subtle)" />
                            <XAxis dataKey="model" tick={{ fill: 'var(--bm-color-text-dimmed)', fontSize: 9 }} tickLine={false} axisLine={false} interval={0} />
                            <YAxis tickFormatter={(v: number) => usd(v)} tick={{ fill: 'var(--bm-color-text-dimmed)', fontSize: 10 }} tickLine={false} axisLine={false} width={52} />
                            <Tooltip
                                formatter={(value: number, name: string) => [usd(value), name]}
                                contentStyle={{ background: 'var(--bm-color-surface)', border: '1px solid var(--bm-color-border)', borderRadius: 'var(--bm-radius)', fontSize: 12 }}
                                cursor={{ fill: 'var(--bm-color-zinc-hover)' }}
                            />
                            <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                            <Bar dataKey="actual" name="Your actual cost" fill={tokens.solid} radius={[4, 4, 0, 0]} />
                            <Bar dataKey="optimized" name="Optimized potential" fill={tokens.soft} radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </SurfaceCard>
                <SurfaceCard $padding="md">
                    <Group gap={6} mb="sm">
                        <Sparkles size={13} color={tokens.solid} />
                        <Text size="xs" c="dimmed" fw={700} tt="uppercase" style={{ fontSize: '9px', letterSpacing: '0.08em' }}>
                            Recommended actions
                        </Text>
                    </Group>
                    <List
                        spacing="md"
                        icon={
                            <ThemeIcon size={18} radius="xl" style={{ background: tokens.soft, color: tokens.text }}>
                                <Box w={6} h={6} style={{ background: tokens.solid, borderRadius: '50%' }} />
                            </ThemeIcon>
                        }
                    >
                        {cost.recommendedActions.map((action) => (
                            <List.Item key={action}>
                                <Text size="sm" style={{ lineHeight: 1.55 }}>
                                    {action}
                                </Text>
                            </List.Item>
                        ))}
                    </List>
                </SurfaceCard>
            </SimpleGrid>
        </Box>
    );
}
